// Shopify storefront helpers used by the public landing page. The customer
// portal is served via the App Proxy from the merchant's storefront domain,
// so /cart/add.js, /products/{handle}.js, and the /checkout permalinks are
// all same-origin and require no auth.
//
// We deliberately do not use the JS Buy SDK or the Storefront API — the
// AJAX endpoints below are stable, public, and free, and they share session
// state with the buyer's existing cart.

import {
  attributionAsCartAttributes,
  getDiscountCode,
} from './landingAttribution';

/**
 * Fetch the full Shopify product JSON for a handle. Returns null if the
 * handle is missing or the request fails — callers fall back to whatever
 * snapshot data they have (saved priceLabel, headline, etc.).
 *
 * The shape is Shopify's standard `/products/{handle}.js` payload: title,
 * handle, vendor, price (cents), compare_at_price (cents), variants[],
 * images[], featured_image, body_html, etc.
 */
export async function fetchProductByHandle(handle) {
  if (!handle || typeof handle !== 'string') return null;
  try {
    const res = await fetch(`/products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!res.ok) return null;
    return await res.json();
  } catch (_) {
    return null;
  }
}

/** Cents → "12.99" formatted with the locale's decimal point. */
export function formatCents(cents) {
  const n = Number(cents);
  if (!Number.isFinite(n)) return '0.00';
  return (n / 100).toFixed(2);
}

/**
 * Resolve the variantId we should put in the cart. Honors a saved override
 * (e.g., the merchant picked "Color: Black" in the editor), else falls back
 * to the first available variant, else the first variant overall.
 */
export function pickVariantId(product, preferredId) {
  if (!product) return null;
  const variants = Array.isArray(product.variants) ? product.variants : [];
  if (preferredId) {
    const hit = variants.find((v) => String(v.id) === String(preferredId));
    // Honor the pin only while it's purchasable — a sold-out pin falls
    // through to the first available variant instead of a guaranteed
    // /cart/add.js 422 on every click.
    if (hit && hit.available !== false) return hit.id;
  }
  const available = variants.find((v) => v.available);
  if (available) return available.id;
  return variants[0]?.id || null;
}

/**
 * POST one or more items to /cart/add.js. Callers redirect to /cart on
 * success so the native Shopify cart UI takes over. We deliberately do not
 * pop a drawer here — the merchant's theme may have its own drawer that
 * fires when the cart changes; sending the buyer to /cart is the
 * lowest-surprise path.
 *
 * Attribution: brella_click_id and utm_* are forwarded as cart attributes
 * so the order/create webhook on the backend can join the click row.
 */
export async function addItemsToCart({ items }) {
  const clean = (Array.isArray(items) ? items : [])
    .filter((it) => it && it.variantId)
    .map((it) => ({
      id: Number(it.variantId),
      quantity: Math.max(1, Number(it.quantity) || 1),
      properties: it.properties || undefined,
    }));
  if (!clean.length) {
    console.warn('[brella] addItemsToCart skipped — no variantIds');
    return { ok: false, error: 'no_variant' };
  }
  const body = { items: clean };
  const attributes = attributionAsCartAttributes();
  try {
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      console.warn('[brella] addItemsToCart failed', res.status, txt);
      return { ok: false, error: `add_failed_${res.status}` };
    }
    if (attributes.length) {
      try {
        await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({
            attributes: attributes.reduce((acc, { name, value }) => {
              acc[name] = value; return acc;
            }, {}),
          }),
        });
      } catch (_) { /* attribution is best-effort */ }
    }
    return { ok: true };
  } catch (e) {
    console.warn('[brella] addItemsToCart threw', e);
    return { ok: false, error: 'network' };
  }
}

/** Single-item convenience wrapper around {@link addItemsToCart}. */
export async function addToCart({ variantId, quantity = 1, properties }) {
  return addItemsToCart({ items: [{ variantId, quantity, properties }] });
}

/**
 * Build a Shopify checkout permalink for a single variant. Used by the
 * "Buy now → 1-tap checkout" button — skips the cart page entirely.
 *
 *   /cart/{variantId}:{qty}?discount={code}&attributes[utm_source]=...
 *
 * Note: Shopify's checkout permalink only respects `?discount=` when the
 * code is for the buyer's logged-in session; for guest buyers we ALSO
 * wrap through /discount/{code}?redirect=/cart/... so the code is staged
 * regardless of session state.
 */
export function buyNowUrl({ variantId, quantity = 1, extraAttributes = [] }) {
  if (!variantId) return '/cart';
  const qty = Math.max(1, Number(quantity) || 1);
  const params = new URLSearchParams();
  // Permalinks can't carry line-item properties, so page-level attribution
  // (e.g. _brella_lp_id) rides as cart attributes here instead.
  const attrs = [...attributionAsCartAttributes(), ...extraAttributes];
  attrs.forEach(({ name, value }) => {
    params.set(`attributes[${name}]`, value);
  });
  const discount = getDiscountCode();
  if (discount) params.set('discount', discount);
  const qs = params.toString();
  const cartPath = `/cart/${encodeURIComponent(variantId)}:${qty}${qs ? `?${qs}` : ''}`;
  if (discount) {
    return `/discount/${encodeURIComponent(discount)}?redirect=${encodeURIComponent(cartPath)}`;
  }
  return cartPath;
}

/**
 * Navigate the browser to the buy-now URL. Pulled into a helper so callers
 * can do `onClick={() => buyNow({variantId, quantity})}` without worrying
 * about whether we're SPA or hard nav.
 */
export function buyNow({ variantId, quantity = 1, extraAttributes = [] }) {
  if (typeof window === 'undefined') return;
  window.location.href = buyNowUrl({ variantId, quantity, extraAttributes });
}
